<template>
  <v-col
    v-if="loaded"
    cols="12"
    class="px-0"
  >
    <block-item-card
      ref="historicalSpectrum"
      :base-currency-symbol="baseCurrency ? baseCurrency.displaySymbol : ''"
      :export-element="elementHistoricalSpectrum"
      :title="$t('NII.Tab.SynthesisRisk.Tab.RiskMethod3HistoricalSpectrum.SecondaryTitle')"
      :title-large="false"
      :title-value="portfolioRisk"
      :title-value-color-red="true"
      :tooltip="$t('NII.Tab.SynthesisRisk.Tab.RiskMethod3HistoricalSpectrum.SecondaryTitle')"
    >
      <template
        v-if="$vuetify.breakpoint.mdAndUp"
        #actions
      >
        <v-spacer />
        <asset-button-group
          :items="assets"
          :selected.sync="selectedAsset"
          class="mt-4"
        />
      </template>
      <template
        v-else
        #actions-row
      >
        <v-card-title
          class="mx-3 pa-0"
          :class="`${aggregateItem ? 'mb-2' : 'mb-4'}`"
        >
          <asset-button-group
            :items="assets"
            :selected.sync="selectedAsset"
          />
        </v-card-title>
        <v-card-title class="mx-3 mb-4 pa-0">
          <asset-button-group
            v-if="aggregateItem"
            :items="aggregateAssets"
            :selected.sync="selectedAggregateAsset"
          />
        </v-card-title>
      </template>

      <template
        v-if="$vuetify.breakpoint.mdAndUp"
        #avatar
      >
        <v-spacer />
        <asset-button-group-orange
          v-if="aggregateItem"
          :items="aggregateAssets"
          :selected.sync="selectedAggregateAsset"
          :mandatory="false"
          class="mt-3"
        />
      </template>

      <v-row class="mb-5">
        <v-col
          cols="12"
        >
          <v-card
            class="pa-2"
            flat
          >
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <e-chart-nii-historical-spectrum
                :axis-pointer-line-style-color="themeColorRGBA('synthesis-ui-blue-06')"
                :axis-pointer-line-style-width="2"
                :axis-x-label-interval="30"
                :axis-x-pointer-label-show="false"
                :axis-x-pointer-show="true"
                :axis-y-label-inside="false"
                :axis-y-label-margin="5"
                :axis-y-tick-prefix="baseCurrency ? baseCurrency.displaySymbol : ''"
                :chart-data-options="chartDataOptions"
                :chart-grid-margin="{left: 10,top: 0,right: 20,bottom: 20,containLabel: true}"
                :chart-height="500"
                :chart-line-smooth="false"
                :chart-scale="true"
                :graph-line-show="true"
              />
            </v-lazy>
          </v-card>
        </v-col>
      </v-row>
    </block-item-card>
  </v-col>
</template>

<script>
import BlockItemCard                  from '@/theme/default/components/common/BlockItemCard'
import AssetButtonGroup               from '@/theme/default/components/common/AssetButtonGroup'
import { clone }                      from '@/lib/utils/helper'
import chartsColorMixin               from '@/theme/default/components/charts/mixins/chartsColorMixin'
import EChartNiiHistoricalSpectrum       from '@/theme/default/components/charts/echarts/EChartNiiHistoricalSpectrum'
import AssetButtonGroupOrange         from '@/theme/default/components/common/AssetButtonGroupOrange.vue'
import API                            from '@/api/Api'
import NIIHistoricalSpectrumResource from '@/api/resources/risks/NIIHistoricalSpectrumResource'

export default {
  name      : 'HistoricalSpectrum',
  components: {
    AssetButtonGroupOrange,
    EChartNiiHistoricalSpectrum,
    BlockItemCard,
    AssetButtonGroup
  },
  directives: {},
  mixins    : [chartsColorMixin],
  props     : {},
  dataStore : {
    niiHistoricalSpectrumData: 'NII.FindHistoricalData',
    niiBaseCurrencyData      : 'NII.FindSummaryData',
    selectedPeriod           : 'NII.SelectedPeriod',
    currentAnalysisId        : 'ActiveAnalysis.currentAnalysisId'
  },
  enums    : {},
  dataModel: null,
  data () {
    return {
      selectedAsset            : null,
      selectedAggregateAsset   : null,
      elementHistoricalSpectrum: null,
      chartYAxisData           : [],
      originalApiData          : null
    }
  },
  computed: {
    baseCurrency () {
      return this.niiBaseCurrencyData?.Data?.BaseCurrency || null
    },

    niiHistoricalSpectrum () {
      return this.niiHistoricalSpectrumData?.Data || null
    },

    loaded () {
      return Object.keys(this.niiHistoricalSpectrumData?.Data || {}).length > 0
    },

    assets () {
      return this.niiHistoricalSpectrum?.Assets || []
    },

    aggregateItem () {
      const v = this.niiHistoricalSpectrum?.Data || []

      return v?.find(item => item.AssetValue === this.selectedAsset)?.AggregateItem || false
    },

    aggregateAssets () {
      const v = this.niiHistoricalSpectrum?.Data || []

      const selectedItem = v?.find(item => item.AssetValue === this.selectedAsset) || null

      return selectedItem?.PerCategoryCodeNames || []
    },

    chartXAxisData () {
      return this.niiHistoricalSpectrum?.Dates || []
    },

    portfolioRisk () {
      const v = this.niiHistoricalSpectrum?.Data || []
      if (v) {
        let selectedItem = v?.find(item => item.AssetValue === this.selectedAsset) || null

        if (this.aggregateItem && this.selectedAggregateAsset) {
          selectedItem = selectedItem?.PerCategoryItems?.find(item => item.AssetValue === this.selectedAggregateAsset) || null
        }

        return selectedItem?.AssetData?.risk || ''
      }

      return ''
    },

    chartDataOptions () {
      return [{
        x: clone(this.chartXAxisData),
        y: clone(this.chartYAxisData)
      }]
    },

    accountId () {
      return this.user?.selectedAccountId || null
    }

  },
  watch: {
    selectedAsset (asset) {
      this.selectedAggregateAsset = null
      this.$set(this, 'chartYAxisData', this.getChartYAxisData(asset, this.selectedAggregateAsset))
    },
    selectedAggregateAsset (aggregateAsset) {
      this.$set(this, 'chartYAxisData', this.getChartYAxisData(this.selectedAsset, aggregateAsset))
    }
  },
  beforeCreate () {},
  created () {
    this.getData()
    this.$bus.$on('nii:period-changed', this.onPeriodChanged)
    this.$bus.$on('nii:cancel-assumptions-edit', this.getData)
  },
  beforeDestroy () {
    this.$bus.$off('nii:period-changed', this.onPeriodChanged)
    this.$bus.$off('nii:cancel-assumptions-edit', this.getData)
  },
  beforeMount () {},
  mounted () {
    this.elementHistoricalSpectrum = this.$refs?.historicalSpectrum?.$el
  },
  beforeUpdate () {},
  updated () {},
  destroyed () {},
  methods: {
    initializeChartYAxisData () {
      this.$set(this, 'chartYAxisData', this.getChartYAxisData(this.selectedAsset, this.selectedAggregateAsset))
    },
    getChartYAxisData (asset, aggregateAsset) {
      const v = this.niiHistoricalSpectrum?.Data || []
      if (v) {
        const selectedItem = v?.find(item => item.AssetValue === asset) || null

        if (!selectedItem?.AggregateItem) {
          return selectedItem?.AssetData || null
        }

        // If aggregate item but no aggregateAsset selected, return parent asset data
        if (!aggregateAsset) {
          return selectedItem?.AssetData || null
        }

        const perCategoryItems = selectedItem?.PerCategoryItems || []

        const selectedAggregateItem = perCategoryItems?.find(item => item.AssetValue === aggregateAsset) || null
        return selectedAggregateItem?.AssetData || null
      }

      return []
    },

    getData () {
      API.Resource.NII.FindHistoricalData(this.accountId, this.currentAnalysisId)
        .then(response => {
          this.handleResponse(response)
        })
        .catch(e => {
          this.handleResponse(e.response)
        })
    },
    handleResponse (response) {
      const data = API.responseData(response) || null
      const errors = API.responseErrors(response) || []
      const result = data?.findNiiAnalysis || null

      if (API.isResponseSuccess(response) && result) {
        // Store the original API data for period switching
        this.originalApiData = clone(result)

        const o = clone(result)
        o.selectedPeriod = this.selectedPeriod
        const v = new NIIHistoricalSpectrumResource(o)

        this.$set(this.niiHistoricalSpectrumData, 'Data', v)
        this.initializeChartYAxisData()
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },

    onPeriodChanged (newPeriod) {
      // Recreate the resource with the new selectedPeriod from original API data
      if (this.originalApiData) {
        const o = clone(this.originalApiData)
        o.selectedPeriod = newPeriod
        const v = new NIIHistoricalSpectrumResource(o)
        this.$set(this.niiHistoricalSpectrumData, 'Data', v)
      }
    }

  }
}
</script>

<style scoped>

</style>
